<template>
  <transition name="create-checkpoint-dialog" appear>
    <div
      v-if="dialog"
      class="create-checkpoint-dialog__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-checkpoint-dialog-title"
      @mousedown.self="onCancel"
    >
      <div class="create-checkpoint-dialog__card">
        <div class="create-checkpoint-dialog__header">
          <span id="create-checkpoint-dialog-title" class="create-checkpoint-dialog__title">
            {{ dialogTitle }}
          </span>
          <button
            type="button"
            class="create-checkpoint-dialog__close-btn"
            aria-label="Close"
            @click="onCancel"
          >
            <Icon name="popup_close" color="dark" size="normal" />
          </button>
        </div>

        <div class="create-checkpoint-dialog__content">
          <div v-if="showLevelAndCriteria" class="create-checkpoint-dialog__level-banner">
            <CheckpointLevelBanner
              class="create-checkpoint-dialog__level-banner-cmp"
              :level="bannerLevelName"
              selector-label-suffix=" Level"
              @update:level="onBannerLevelUpdate"
            />
          </div>

          <CriteriaInput
            v-if="showLevelAndCriteria"
            class="create-checkpoint-dialog__criteria"
            :criteria="criteria"
            :criteriaPrefix="criteriaPrefix"
            :dropdownItems="dropdownItems"
            @update:criteria="criteria = $event"
            @update:criteriaPrefix="criteriaPrefix = $event"
          />

          <div
            class="create-checkpoint-dialog__options"
            role="radiogroup"
            aria-label="Microlearning type"
          >
            <div
              v-for="option in visibleOptions"
              :key="option.key"
              class="create-checkpoint-dialog__option"
              :class="{
                'create-checkpoint-dialog__option--selected': selectedType === option.key,
                'create-checkpoint-dialog__option--accent': option.accent,
              }"
              role="radio"
              :aria-checked="selectedType === option.key"
              :tabindex="0"
              @click="selectType(option.key)"
              @keydown.enter.prevent="selectType(option.key)"
              @keydown.space.prevent="selectType(option.key)"
            >
              <span class="create-checkpoint-dialog__radio" aria-hidden="true">
                <img
                  v-if="selectedType === option.key"
                  class="create-checkpoint-dialog__radio-checked"
                  src="@components/assets/checkedRadioIconRing.svg"
                  alt=""
                  width="16"
                  height="16"
                />
              </span>
              <span class="create-checkpoint-dialog__option-text">
                <span class="create-checkpoint-dialog__option-title">
                  {{ option.title }}
                  <span v-if="option.hint" class="create-checkpoint-dialog__option-hint"
                    >({{ option.hint }})</span
                  >
                </span>
                <span v-if="option.desc" class="create-checkpoint-dialog__option-desc">{{
                  option.desc
                }}</span>
              </span>
            </div>
          </div>

          <div class="create-checkpoint-dialog__actions">
            <StandardButton
              label="Cancel"
              type="magenta"
              variant="outline"
              size="normal"
              @click="onCancel"
            />
            <StandardButton
              label="Next"
              type="magenta"
              variant="primary"
              size="normal"
              :disabled="!canProceed"
              @click="onNext"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import "./createCheckpointDialog.css";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import CheckpointLevelBanner from "@components/CheckpointLevelBanner/CheckpointLevelBanner.vue";
import CriteriaInput from "@components/CriteriaInput/CriteriaInput.vue";
import { maybeNormalizeCriteriaBodyEn } from "@components/utils/normalizeCriteriaBodyEn";
import store from "@/store";

const BANNER_LEVEL_BY_NAME = {
  Apprentice: { id: 1, name: "Apprentice", value: "apprentice" },
  Professional: { id: 2, name: "Professional", value: "professional" },
  Coach: { id: 3, name: "Coach", value: "coach" },
  Master: { id: 4, name: "Master", value: "master" },
};

/** Show Task an Employee to Upload a Video in create and propose-changes. */
const SHOW_TASK_EMPLOYEE_UPLOAD_OPTION = true;

/** Keys match API `proposed_microlearning_type` values. */
const CREATE_OPTIONS = [
  {
    key: "video",
    title: "Upload a Video",
    hint: "Max 5 minutes duration",
    desc: "Most Checkpoints use a video for microlearning",
  },
  {
    key: "task_an_employee",
    title: "Task an Employee to Upload a Video",
    hint: "To be done on the mobile app",
    desc: "An Employee will receive a task to Upload a video for this checkpoint",
  },
  {
    key: "article",
    title: "Start a knowledge article",
    hint: "to be edited with a rich editor",
    desc: "Articles are great for knowledge only checkpoints",
  },
  {
    key: "inspector_only",
    title: "No learning content needed",
    hint: "Inspector Only",
    desc: "An inspector will use their judgment to determine completion using only the checkpoint criteria.",
    accent: true,
  },
];

/** Active checkpoint → Propose Changes option set (Figma). */
const PROPOSE_CHANGES_OPTIONS = [
  {
    key: "copy_active",
    title: "Copy content from the active checkpoint",
    hint: "",
    desc: "Do this when you want to make small changes",
  },
  {
    key: "video",
    title: "Upload a Video",
    hint: "Max 5 minutes duration",
    desc: "Most Checkpoints use a video for microlearning",
  },
  {
    key: "task_an_employee",
    title: "Task an Employee to Upload a Video",
    hint: "To be done on the mobile app",
    desc: "An Employee will receive a task to Upload a video for this checkpoint",
  },
  {
    key: "article",
    title: "Start a knowledge article",
    hint: "to be edited with a rich editor",
    desc: "Articles are great for knowledge only checkpoints",
  },
  {
    key: "inspector_only",
    title: "Start from scratch as an inspector only",
    hint: "",
    desc: "",
  },
];

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  levelProp: {
    type: Object,
    default: () => ({ id: 1, name: "Apprentice", value: "apprentice" }),
  },
  criteriaProp: {
    type: String,
    default: "",
  },
  criteriaPrefixProp: {
    type: String,
    default: "Can",
  },
  /** Restore selection when reopening (e.g. Back from TaskEmployee dialog). */
  selectedTypeProp: {
    type: String,
    default: "",
  },
  /**
   * Hide type option keys (e.g. omit `inspector_only` when converting an
   * existing inspector-only checkpoint to microlearning).
   */
  omitTypes: {
    type: Array,
    default: () => [],
  },
  /**
   * Type picker only (no level banner / criteria). Used when adding
   * microlearning to an existing inspector-only checkpoint.
   */
  typeOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * `create` — choose microlearning type (create / add flow).
   * `propose-changes` — active checkpoint propose-changes option set.
   */
  mode: {
    type: String,
    default: "create",
    validator: (v) => ["create", "propose-changes"].includes(v),
  },
});

const emit = defineEmits(["cancel", "next"]);

const DEFAULT_SELECTED_BY_MODE = {
  create: "video",
  "propose-changes": "copy_active",
};

const selectedType = ref(DEFAULT_SELECTED_BY_MODE.create);
const criteria = ref("");
const criteriaPrefix = ref("Can");
const level = ref({ id: 1, name: "Apprentice", value: "apprentice" });
const dropdownItems = ref(["Can", "Does", "Demonstrated", "Proved"]);

const isProposeChangesMode = computed(() => props.mode === "propose-changes");

const showLevelAndCriteria = computed(() => !props.typeOnly && !isProposeChangesMode.value);

const omitTypeSet = computed(() => new Set((props.omitTypes || []).map(String)));

const modeOptions = computed(() =>
  isProposeChangesMode.value ? PROPOSE_CHANGES_OPTIONS : CREATE_OPTIONS
);

const visibleOptions = computed(() =>
  modeOptions.value.filter((option) => {
    if (!SHOW_TASK_EMPLOYEE_UPLOAD_OPTION && option.key === "task_an_employee") {
      return false;
    }
    return !omitTypeSet.value.has(option.key);
  })
);

const dialogTitle = computed(() => {
  if (isProposeChangesMode.value) return "Propose Changes";
  if (props.typeOnly) return "Choose a Microlearning type";
  return "Choose a Microlearning type to start";
});

const bannerLevelName = computed(() => {
  const n = level.value?.name;
  return n && BANNER_LEVEL_BY_NAME[n] ? n : "Apprentice";
});

const defaultSelectedType = computed(
  () => DEFAULT_SELECTED_BY_MODE[props.mode] || DEFAULT_SELECTED_BY_MODE.create
);

const canProceed = computed(() => {
  if (!selectedType.value) return false;
  if (props.typeOnly || isProposeChangesMode.value) return true;
  return Boolean(String(criteriaPrefix.value || "").trim() && String(criteria.value || "").trim());
});

function resolveInitialSelectedType() {
  const preferred = props.selectedTypeProp || defaultSelectedType.value;
  if (visibleOptions.value.some((option) => option.key === preferred)) {
    return preferred;
  }
  return visibleOptions.value[0]?.key || defaultSelectedType.value;
}

function onBannerLevelUpdate(nextName) {
  const mapped = BANNER_LEVEL_BY_NAME[nextName];
  if (mapped) {
    level.value = mapped;
  }
}

function selectType(key) {
  selectedType.value = key;
}

function resetState() {
  selectedType.value = resolveInitialSelectedType();
  criteria.value = props.criteriaProp || "";
  criteriaPrefix.value = props.criteriaPrefixProp || "Can";
  level.value = props.levelProp || { id: 1, name: "Apprentice", value: "apprentice" };
}

function onCancel() {
  emit("cancel");
}

function onNext() {
  if (!canProceed.value) return;
  const ideaMDP452 = Boolean(store.getters["featureFlags/ideaMDP452"]);
  const normalizedCriteria = maybeNormalizeCriteriaBodyEn(criteria.value, ideaMDP452);
  if (normalizedCriteria !== criteria.value) {
    criteria.value = normalizedCriteria;
  }
  emit("next", {
    type: selectedType.value,
    level: level.value,
    criteria: normalizedCriteria,
    prefix: criteriaPrefix.value,
  });
}

watch(
  () => props.dialog,
  (val) => {
    if (val) resetState();
  },
  { immediate: true }
);
</script>
